"use client";

import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllBlogs, setSelectedTitle } from "../config/redux/slices/blogSlice";
import Head from "next/head";
import { useRouter } from "next/navigation";

/* ------------------ UI STATES ------------------ */
const SkeletonLoader = () => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[1, 2, 3, 4, 5, 6].map(i => (
      <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm">
        <div className="h-48 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse" />
        <div className="p-6 space-y-3">
          <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
          <div className="h-4 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6" />
        </div>
      </div>
    ))}
  </div>
);

const ErrorComponent = ({ onRetry }) => (
  <div className="text-center py-20">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
      <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </div>
    <p className="text-gray-700 mb-6 text-lg">Oops! Something went wrong</p>
    <button
      onClick={onRetry}
      className="px-8 py-3 bg-[#00489b] text-white rounded-full font-medium hover:bg-[#003366] transition-all shadow-lg hover:shadow-xl"
    >
      Try Again
    </button>
  </div>
);

const EmptyState = () => (
  <div className="text-center py-32">
    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-6">
      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
      </svg>
    </div>
    <p className="text-gray-500 text-xl">No blogs available yet</p>
    <p className="text-gray-400 mt-2">Check back soon for fresh content!</p>
  </div>
);

const NoResults = ({ title, clear }) => (
  <div className="text-center py-20">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
      <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </div>
    <p className="mb-2 text-gray-700 text-lg">No blogs found for</p>
    <p className="text-2xl font-bold text-[#00489b] mb-6">"{title}"</p>
    <button
      onClick={clear}
      className="px-8 py-3 bg-[#00489b] text-white rounded-full font-medium hover:bg-[#003366] transition-all"
    >
      Clear Filter
    </button>
  </div>
);

/* ------------------ PAGINATION ------------------ */
const Pagination = React.memo(({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const showEllipsis = totalPages > 7;

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="flex flex-col items-center gap-4 mt-12">
      {/* Page Info */}
      <div className="text-sm text-gray-600">
        Page <span className="font-semibold text-gray-900">{currentPage}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center gap-2">
        {/* Previous Button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          aria-label="Previous page"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Previous</span>
        </button>

        {/* Page Numbers */}
        <div className="flex items-center gap-2">
          {getPageNumbers().map((page, idx) => (
            page === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-3 py-2 text-gray-400">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`min-w-[40px] px-4 py-2 rounded-lg font-medium transition-all ${currentPage === page
                  ? 'bg-[#00489b] text-white shadow-lg scale-110'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-[#00489b]'
                  }`}
                aria-label={`Go to page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            )
          ))}
        </div>

        {/* Next Button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          aria-label="Next page"
        >
          <span className="hidden sm:inline">Next</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
});

/* ------------------ BLOG CARD ------------------ */
const BlogCard = React.memo(({ blog, onClick }) => {
  const firstContent = blog.content?.[0];
  const description = firstContent?.description || firstContent?.content || "";

  return (
    <article
      onClick={() => onClick(blog.slug)}
      className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#00489b]/20"
    >
      {/* Gradient Header */}
      <div className="h-40 bg-gradient-to-br from-[#00489b] via-[#0066cc] to-[#0088ff] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTEwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHptMC0xMGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        <div className="absolute bottom-4 left-6 right-6">
          <div className="flex items-center gap-2 text-white/90 text-sm">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            <span>{new Date(blog.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h2 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-[#00489b] transition-colors line-clamp-2">
          {blog.title}
        </h2>

        <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
          {description.slice(0, 150)}...
        </p>

        {/* Keywords */}
        {blog.keywords && blog.keywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {blog.keywords.slice(0, 3).map((keyword, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full"
              >
                {keyword}
              </span>
            ))}
          </div>
        )}

        {/* Read More */}
        <div className="flex items-center text-[#00489b] font-semibold group-hover:gap-2 transition-all">
          <span>Read Article</span>
          <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>
      </div>
    </article>
  );
});

/* ------------------ MAIN PAGE ------------------ */
export default function BlogList() {
  const dispatch = useDispatch();
  const router = useRouter();

  const { list: blogs, loading, error, selectedTitle } = useSelector(
    state => state.blog
  );

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 9; // 3x3 grid

  useEffect(() => {
    dispatch(fetchAllBlogs());
  }, [dispatch]);

  // Filter blogs based on selected title
  const filteredBlogs = useMemo(() => {
    if (!selectedTitle) return blogs;
    return blogs.filter(blog =>
      blog.title?.toLowerCase().includes(selectedTitle.toLowerCase())
    );
  }, [blogs, selectedTitle]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTitle]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredBlogs.length / ITEMS_PER_PAGE);

  const paginatedBlogs = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredBlogs.slice(startIndex, endIndex);
  }, [filteredBlogs, currentPage]);

  const showNoResults = filteredBlogs.length === 0 && !!selectedTitle;

  const handleBlogClick = useCallback(
    slug => router.push(`/blog-sortkaro/${slug}`),
    [router]
  );

  const clearFilter = () => dispatch(setSelectedTitle(""));

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of blog list
    document.getElementById('blog-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const pageTitle = "SortKaro Blog – Expert Home Care & Lifestyle Tips";
  const pageDescription =
    "Discover expert home care, cleaning, and lifestyle tips by SortKaro. Simple guides for smarter living in India.";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "SortKaro Blog",
    description: pageDescription,
    url: "https://www.sortkaro.com/blog-sortkaro",
  };

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://www.sortkaro.com/blog-sortkaro" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* ------------------ HERO SECTION ------------------ */}
        <section className="relative bg-gradient-to-br from-[#00489b] via-[#0066cc] to-[#0088ff] text-white overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTEwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHptMC0xMGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>

          <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28 text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium">
              ✨ Your Home Care Knowledge Hub
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight">
              SortKaro Blog
            </h1>

            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
              Expert home care tips, cleaning hacks, and lifestyle guides crafted for Indian households.
              Simple, practical, and easy to follow.
            </p>

            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="#blog-list"
                className="px-8 py-4 bg-white text-[#00489b] rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
              >
                Explore Articles
              </a>
              <a
                href="/"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-full font-semibold hover:bg-white/20 transition-all"
              >
                Book Services
              </a>
            </div>
          </div>

          {/* Wave Divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white" />
            </svg>
          </div>
        </section>

        {/* ------------------ VALUE PROPS ------------------ */}
        <section className="max-w-6xl mx-auto px-4 -mt-8 relative z-10">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
                title: "Expert Tips",
                desc: "Professional advice from home care specialists"
              },
              {
                icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
                title: "Easy to Follow",
                desc: "Simple language, step-by-step guides"
              },
              {
                icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
                title: "India-Focused",
                desc: "Tailored for Indian homes and climate"
              }
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#00489b] to-[#0066cc] rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ------------------ FEATURED INSIGHTS ------------------ */}
        <section className="max-w-6xl mx-auto px-4 py-16">
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Tip Box */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-l-4 border-blue-500 p-6 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="font-bold text-blue-900 mb-2">💡 Pro Tip</div>
                  <div className="text-blue-800">Open windows during non-rainy hours and use moisture absorbers in wardrobes to prevent mildew during monsoon.</div>
                </div>
              </div>
            </div>

            {/* Note Box */}
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-l-4 border-amber-500 p-6 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="font-bold text-amber-900 mb-2">📌 Quick Note</div>
                  <div className="text-amber-800">Book appliance servicing early in monsoon—same-day slots fill fast in Gurugram and Delhi NCR.</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quote */}
          <blockquote className="bg-white border-l-4 border-[#00489b] p-8 rounded-xl shadow-sm">
            <div className="flex items-start gap-4">
              <svg className="w-10 h-10 text-[#00489b] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
              <div>
                <p className="text-gray-700 text-lg italic mb-3 leading-relaxed">
                  A clean and organized home is the foundation of a healthy family. Small preventive steps today save big costs tomorrow.
                </p>
                <p className="text-gray-500 font-medium">— SortKaro Team</p>
              </div>
            </div>
          </blockquote>
        </section>

        {/* ------------------ BLOG LIST ------------------ */}
        <section id="blog-list" className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Latest Articles
              </h2>
              <p className="text-gray-600">
                {filteredBlogs.length} {filteredBlogs.length === 1 ? 'article' : 'articles'} available
                {filteredBlogs.length > ITEMS_PER_PAGE && (
                  <span className="text-gray-400"> • Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredBlogs.length)}</span>
                )}
              </p>
            </div>

            {selectedTitle && (
              <button
                onClick={clearFilter}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Clear Filter
              </button>
            )}
          </div>

          {loading && <SkeletonLoader />}
          {error && <ErrorComponent onRetry={() => dispatch(fetchAllBlogs())} />}
          {!loading && !error && blogs.length === 0 && <EmptyState />}

          {!loading && !error && filteredBlogs.length > 0 && (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedBlogs.map(blog => (
                  <BlogCard
                    key={blog.id}
                    blog={blog}
                    onClick={handleBlogClick}
                  />
                ))}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </>
          )}

          {showNoResults && <NoResults title={selectedTitle} clear={clearFilter} />}
        </section>

        {/* ------------------ CTA SECTION ------------------ */}
        {/* <section className="max-w-6xl mx-auto px-4 py-16">
          <div className="bg-gradient-to-br from-[#00489b] to-[#0066cc] rounded-3xl p-12 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTEwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHptMC0xMGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>

            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Transform Your Home?
              </h2>
              <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
                Book professional home services in Gurugram with SortKaro. Trusted, verified, and hassle-free.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href="/"
                  className="px-8 py-4 bg-white text-[#00489b] rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                >
                  Book Now
                </a>
                <a
                  href="/about-sortkaro"
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-full font-semibold hover:bg-white/20 transition-all"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </section> */}
      </div>
    </>
  );
}
